import { useState } from 'react';
import {
  Box, Button, Chip, IconButton, Popover, TextField, Typography,
} from '@mui/material';
import { Tune, Add, Close } from '@mui/icons-material';

interface Props {
  stresses: number[];
  onChange: (stressesKpa: number[]) => void;
}

/**
 * Rock Direct Shear normal stresses (kPa) — the workbook rule: quantity 1,
 * up to 5 stresses listed alongside. Chip list + popover editor.
 */
export const StressesEditor = ({ stresses, onChange }: Props) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [entry, setEntry] = useState('');

  const addEntry = () => {
    const v = Number(entry);
    if (!Number.isFinite(v) || v <= 0 || stresses.length >= 5) return;
    onChange([...stresses, v]);
    setEntry('');
  };

  // 2026-08-06 (Vahid): stresses are OPTIONAL at intake — no error styling
  // when empty; the label invites rather than demands.
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="primary"
        startIcon={<Tune sx={{ fontSize: 14 }} />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none', fontSize: 11, py: 0, px: 0.75, minWidth: 0, whiteSpace: 'nowrap' }}
      >
        {stresses.length > 0 ? `${stresses.join(', ')} kPa` : 'Stresses (optional)'}
      </Button>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 280 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Direct Shear normal stresses (kPa)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Up to 5 normal stresses (kPa) for this Direct Shear test — optional, the lab will confirm them with you.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5, minHeight: 28 }}>
            {stresses.map((s, i) => (
              <Chip
                key={`${s}-${i}`}
                label={`${s} kPa`}
                size="small"
                onDelete={() => onChange(stresses.filter((_, j) => j !== i))}
                deleteIcon={<Close />}
              />
            ))}
            {stresses.length === 0 && (
              <Typography variant="caption" color="text.secondary">None yet (optional).</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              type="number"
              label="kPa"
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEntry(); } }}
              inputProps={{ min: 1, step: 'any', autoComplete: 'off' }}
              disabled={stresses.length >= 5}
              fullWidth
            />
            <IconButton onClick={addEntry} disabled={!entry || stresses.length >= 5} color="primary">
              <Add />
            </IconButton>
          </Box>
        </Box>
      </Popover>
    </>
  );
};
